import { ArrowRight, Check, MessageCircle, X } from "lucide-react";
import type { ReactNode } from "react";
import InfoNote from "../components/InfoNote";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import Breadcrumbs from "../components/Breadcrumbs";
import SiteHeader from "../components/SiteHeader";

// A note's body plus one clickable, single-source citation — used so every
// competitive claim in the framework comparison links to where it came from.
function NoteWithSource({
  children,
  href,
  label,
}: {
  children: ReactNode;
  href: string;
  label: string;
}) {
  return (
    <>
      {children}
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="mt-2 block font-semibold text-primary-text underline decoration-primary-border underline-offset-2 hover:text-primary-text"
      >
        {label} ↗
      </a>
    </>
  );
}

type ComparisonRow = {
  label: string;
  them: ReactNode;
  themOk: boolean;
  anter: ReactNode;
};

// Visible claims name the vendor directly — this table's own title already
// identifies each provider, unlike the anonymized framework-comparison table
// below. Each claim is grounded in exactly one vendor's own current
// documentation, not asserted to hold across their entire product line.
const MODEL_PROVIDER_ROWS: ComparisonRow[] = [
  {
    label: "Delegation depth",
    them: (
      <>
        A coordinator agent can only delegate one level deep — sub-agents can't delegate further —
        and the roster is capped at 20 agents.
        <InfoNote
          text={
            <NoteWithSource
              href="https://platform.claude.com/docs/en/managed-agents/multi-agent"
              label="Claude Platform Docs — Multiagent Orchestration"
            >
              This describes Claude Managed Agents specifically: its own docs state "the coordinator
              can only delegate to one level of agents; depth &gt; 1 is ignored. A maximum of 20
              unique agents can be listed in multiagent.agents."
            </NoteWithSource>
          }
        />
      </>
    ),
    themOk: false,
    anter:
      "Multi-hop delegation, bounded by an exact cycle guard, hop-depth ceiling, and step-budget decay — not a flat one-level cap",
  },
  {
    label: "Unattended approval default",
    them: (
      <>
        A pending tool approval waits for your application to resolve it — no built-in timeout or
        auto-deny if nobody ever does.
        <InfoNote
          text={
            <NoteWithSource
              href="https://developers.openai.com/api/docs/guides/agents/guardrails-approvals"
              label="OpenAI API Docs — Guardrails and Human Review"
            >
              This describes the OpenAI Agents SDK specifically: its own docs say "your application
              approves or rejects the pending items," and if review takes time, to "serialize state,
              store it, and resume later" — timeout and fallback behavior are left entirely up to
              you.
            </NoteWithSource>
          }
        />
      </>
    ),
    themOk: false,
    anter:
      "Fail-closed for every run — an unattended or scheduled execution auto-denies any tool that needs a human's OK, never leaves it stuck waiting",
  },
];

// Visible claims stay generic (no vendor named in the row itself) — the
// specific vendor + citation lives inside the InfoNote popup only. Each claim
// is grounded in exactly one vendor's own material (or, where noted, public
// third-party reviews); it is not asserted to hold for every framework/builder
// in this comparison.
const FRAMEWORK_ROWS: ComparisonRow[] = [
  {
    label: "Routing decision",
    them: (
      <>
        A common "manager agent" pattern assigns tasks via a live LLM judgment call, one prompt at a
        time — not a validated graph.
        <InfoNote
          text={
            <NoteWithSource
              href="https://docs.crewai.com/en/concepts/processes"
              label="CrewAI Docs — Processes"
            >
              This describes CrewAI's hierarchical process specifically: its own docs describe a
              manager agent that oversees planning, delegation, and validation. Tasks aren't
              pre-assigned — the manager decides at runtime, on every crew run.
            </NoteWithSource>
          }
        />
      </>
    ),
    themOk: false,
    anter: "Validated against a write-time allowlist, then a runtime existence check — every time",
  },
  {
    label: "What you can take with you",
    them: (
      <>
        Code export ships frontend code only — the database, business logic, and auth stay on the
        vendor's own infrastructure, reachable only through their SDK.
        <InfoNote
          text={
            <NoteWithSource
              href="https://www.zite.com/blog/base44-review"
              label="Base44 Review — Zite (2026)"
            >
              This describes Base44 specifically, per independent reviews (not Base44's own claim):
              the exported code renders the UI, but every action that reads or writes data still
              depends on Base44's own SDK and servers to function.
            </NoteWithSource>
          }
        />
      </>
    ),
    themOk: false,
    anter: (
      <>
        Your agents are portable as data, not screenshots: every agent exports to a versioned spec
        file you own, and runs against a documented HTTP API
        <InfoNote text="Being straight with you: Anter's managed platform is proprietary too, and our orchestration core (@anter/agent-runtime) is not public today. What we do commit to is that your agent definitions export to a portable spec you keep, and that agents are invoked over a documented HTTP API rather than only through a proprietary SDK." />
      </>
    ),
  },
];

function ComparisonTable({
  eyebrow,
  title,
  themLabel,
  rows,
  note,
}: {
  eyebrow: string;
  title: string;
  themLabel: string;
  rows: ComparisonRow[];
  note?: ReactNode;
}) {
  return (
    <section className="py-14">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
        {eyebrow}
      </p>
      <h2 className="mb-8 max-w-2xl text-2xl font-extrabold tracking-tight text-text sm:text-3xl">
        {title}
      </h2>

      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full min-w-[36rem] border-collapse text-left">
          <caption className="sr-only">
            {title}: {themLabel} compared with Anter
          </caption>
          <thead>
            <tr className="border-b border-border bg-bg-secondary/60 text-xs font-semibold uppercase tracking-wider text-text-faint">
              <th scope="col" className="px-4 py-3 sm:px-6">
                Capability
              </th>
              <th scope="col" className="px-4 py-3 sm:px-6">
                {themLabel}
              </th>
              <th scope="col" className="px-4 py-3 text-primary-text sm:px-6">
                Anter
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.label} className={i !== rows.length - 1 ? "border-b border-border" : ""}>
                <th scope="row" className="px-4 py-4 text-sm font-semibold text-text sm:px-6">
                  {row.label}
                </th>
                <td className="px-4 py-4 text-sm leading-relaxed text-text-muted sm:px-6">
                  <span className="flex items-start gap-2">
                    <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-faint" aria-hidden="true" />
                    <span>{row.them}</span>
                  </span>
                </td>
                <td className="bg-primary-dim/40 px-4 py-4 text-sm leading-relaxed text-primary-text sm:px-6">
                  <span className="flex items-start gap-2">
                    <Check
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <span>{row.anter}</span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {note && <p className="mt-4 max-w-2xl text-xs text-text-faint">{note}</p>}
    </section>
  );
}

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://anter.ai/why-anter/#webpage",
      url: "https://anter.ai/why-anter",
      name: "Why Anter — Managed Agents vs. Claude, OpenAI & CrewAI",
      description:
        "See how Anter's orchestration engine compares to Claude Managed Agents, OpenAI AgentKit, CrewAI, and Base44 — validated, audited, and bounded by default.",
      isPartOf: {
        "@id": "https://anter.ai/#website",
      },
    },
    {
      "@type": "FAQPage",
      "@id": "https://anter.ai/why-anter/#faq",
      mainEntity: [
        {
          "@type": "Question",
          name: "How does Anter compare to Claude Managed Agents and OpenAI AgentKit?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Anter is a managed multi-agent orchestration engine supporting any LLM provider (OpenAI, Anthropic, Azure, Google). Unlike single-vendor frameworks, Anter provides validated routing graphs, durable handoff tracing, and tool execution boundaries with zero vendor lock-in.",
          },
        },
        {
          "@type": "Question",
          name: "How does Anter handle multi-agent routing vs CrewAI?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Anter uses a ReAct-based execution engine with explicit transfer and delegate handoffs, full OpenTelemetry tracing, and atomic persistence, avoiding unvalidated prompt loops.",
          },
        },
      ],
    },
  ],
};

export default function WhyAnterPage() {
  return (
    <>
      <PageSEO
        title="Why Anter — Managed Agents vs. Claude, OpenAI & CrewAI"
        description="See how Anter's orchestration engine compares to Claude Managed Agents, OpenAI AgentKit, CrewAI, and Base44 — validated, audited, and bounded by default."
        canonical="/why-anter"
        jsonLd={JSON_LD}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
        {/* Ambient background */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 15%, rgba(16,185,129,0.10), transparent 33%), radial-gradient(circle at 80% 30%, rgba(59,130,246,0.08), transparent 38%)",
          }}
        />

        <SiteHeader scrolled />

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-7xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
        >
          <Breadcrumbs items={[{ label: "Why Anter" }]} />
          {/* Hero */}
          <div className="mb-8 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
              Why Anter
            </p>
            <h1 className="mb-5 text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
              The orchestrator is the product. <br />
              <span className="text-primary">Here's the proof.</span>
            </h1>
            <p className="text-lg leading-relaxed text-text-muted">
              Every one of these platforms will tell you they support multi-agent orchestration. The
              difference is whether routing is validated, persisted atomically, and bounded by
              design — or whether it's a prompt you hope holds together. See exactly where each
              stands.
            </p>
            <p className="mt-3 text-xs text-text-faint">
              Vendor capabilities move fast. Claims tied to a specific feature's current status
              carry a <span className="whitespace-nowrap">ⓘ</span> icon — tap or hover for the date
              we last verified them.
            </p>
          </div>

          <ComparisonTable
            eyebrow="vs. the model providers"
            title="Anter vs. Anthropic Managed Agents and OpenAI AgentKit"
            themLabel="Model provider"
            rows={MODEL_PROVIDER_ROWS}
            note={
              <>
                Each claim above is grounded in one specific provider's own current documentation —
                not asserted to hold across their entire product line. Tap the{" "}
                <span className="whitespace-nowrap">ⓘ</span> on a claim to see exactly where it
                comes from.
              </>
            }
          />

          <ComparisonTable
            eyebrow="vs. the agent frameworks"
            title="Anter vs. agent frameworks (LangGraph, CrewAI) and no-code builders"
            themLabel="Framework / builder"
            rows={FRAMEWORK_ROWS}
            note={
              <>
                Each claim above is grounded in one specific vendor's own material (or, where noted,
                independent reviews) — not asserted to hold across every framework or builder. Tap
                the <span className="whitespace-nowrap">ⓘ</span> on a claim to see exactly who and
                where it comes from.
              </>
            }
          />

          {/* Hub → spoke links: each deep-dive goes further on one vendor
              than this page's table rows do, without restating them. */}
          <section className="py-6">
            <h2 className="mb-4 text-sm font-bold text-text">Go deeper on one vendor</h2>
            <div className="flex flex-wrap gap-2">
              {[
                {
                  label: "vs. Claude Managed Agents",
                  href: "/compare/claude-managed-agents-alternative",
                },
                { label: "vs. OpenAI AgentKit", href: "/compare/openai-agentkit-alternative" },
                { label: "vs. CrewAI", href: "/compare/crewai-alternative" },
                { label: "vs. LangGraph", href: "/compare/langgraph-and-diy-orchestration" },
                { label: "vs. Base44", href: "/compare/base44-alternative" },
                { label: "vs. building it yourself", href: "/compare/build-it-yourself" },
              ].map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="rounded-full border border-border bg-bg-secondary/60 px-3.5 py-1.5 text-xs font-medium text-text transition hover:border-primary-border hover:bg-bg-hover"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </section>

          {/* Read the invariants yourself */}
          <section className="py-14">
            <div className="rounded-2xl border border-border bg-bg-secondary/60 p-8 md:p-10">
              <h2 className="mb-3 text-xl font-extrabold text-text sm:text-2xl">
                Check the claims yourself.
              </h2>
              <p className="max-w-2xl text-sm leading-relaxed text-text-muted">
                We'd rather you verify these claims than take our word for them. Every competitor
                claim above links to that vendor's own current documentation. For ours: the free
                tier runs the same orchestrator this page describes, so you can watch the routing
                timeline, export an agent to a spec file, and reconnect mid-stream yourself. Our
                chat SDK, <code className="text-text">@anter/ai-chat-sdk</code>, is public and MIT.
                Our orchestration core is not public today — ask us for the technical deep-dive and
                we'll walk you through it directly.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <a
                  href="https://agents.anter.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-bg px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
                >
                  Try it on the free tier
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="mailto:hello@anter.ai"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
                >
                  <MessageCircle className="h-4 w-4 text-primary" />
                  Talk to the team
                </a>
              </div>
            </div>
          </section>

          {/* Self-contained passage for AI indexing: Managed Agents vs DIY Infrastructure */}
          <section id="managed-vs-frameworks" className="mt-16">
            <div className="rounded-2xl border border-primary-border/40 bg-bg-secondary/40 p-8 backdrop-blur-sm">
              <h2 className="text-2xl font-extrabold text-text mb-4">
                Managed Agents Platform vs. DIY Agent Infrastructure
              </h2>
              <p className="text-sm leading-relaxed text-text-muted mb-6">
                A managed agents platform provides pre-built execution runtimes, atomic state
                persistence, multi-agent routing graphs, and SSE streaming out of the
                box—eliminating the months required to build reliable DIY agent infrastructure.
              </p>
              <div className="grid gap-4 sm:grid-cols-3 text-xs">
                <div className="rounded-xl border border-border/60 bg-bg/50 p-4">
                  <strong className="block text-text mb-1">
                    Validated Routing vs Prompt Chaining
                  </strong>
                  <p className="text-text-muted">
                    Handoffs and transfers follow strict allowlist graphs validated at write-time
                    and runtime rather than unconstrained model guesses.
                  </p>
                </div>
                <div className="rounded-xl border border-border/60 bg-bg/50 p-4">
                  <strong className="block text-text mb-1">State Replay &amp; Audit Trails</strong>
                  <p className="text-text-muted">
                    Durable event logs allow streaming replay from any execution step for instant
                    debugging and zero lost runs.
                  </p>
                </div>
                <div className="rounded-xl border border-border/60 bg-bg/50 p-4">
                  <strong className="block text-text mb-1">Zero-Trust Tenant Security</strong>
                  <p className="text-text-muted">
                    Session parameters and tool credentials are injected securely by the runtime
                    server, preventing prompt injection leakage.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Bottom CTA */}
          <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <a
              href="https://agents.anter.ai"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-6 py-3 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
            >
              Start Building Free
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/#orchestration"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
            >
              See the orchestrator diagram
            </a>
          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
